import filter from 'lodash/filter';
import join from 'lodash/join';
import keys from 'lodash/keys';

import { trackEvent as TrackEvent, identify as JAIdentify } from '@jerry-serverless/analyticslib/frontend';
import { reportConversion } from '@jerry-serverless/analyticslib/partner';

import type { AppData } from '../../types/app_data';
import type { Driver } from '../../types/driver';

import type { Event, AnalyticsUser } from './types';
import { ABTestEventData } from './events/ab_tests';
import { CreateAccountEventData } from './events/create_account';
import { OnboardingEventData } from './events/onboarding';
import { FinishedEventData } from './events/finished';
import { GlobalEventData } from './events/global';
import { YearInReviewEventData } from './events/year_in_review';
import { setUserForHJ } from './hotjar';
import { setUserForFacebookPixel, trackSignedUpForFacebook, trackOnboardedForFacebook } from './facebook';
import { setUserForTikTokPixel, trackSignedUpForTikTok, trackOnboardedForTikTok } from './tiktok';
import { setUserForGoogle, trackSignedUpForGoogle, trackOnboardedForGoogle } from './google';
import { trackSignedUpForTaboola, trackOnboardedForTaboola } from './taboola';
import { trackOnboardedForImpact } from './impact';
import { setUserForTatariPixel, trackSignedUpForTatari, trackOnboardedForTatari } from './tatari';
import { setUserForSnapPixel, trackOnboardedForSnap, trackSignedUpForSnap } from './snap';
import { setUserForRedditPixel, trackOnboardedForReddit, trackSignedUpForReddit } from './reddit';
import { trackSignedUpForPodScribe, trackOnboardedForPodScribe, setLandingForPodScribePixel } from './podscribe';
import { DirverLicenseEventData } from './events/driver_license';
import { RefiDisqualifiedEventData } from './events/refi_disqualified';
import { InviteV31TrackData } from './events/invite_v31';

const DEFAULT_EVENT_CATEGORY = 'Signup';

const EventData = {
  ...ABTestEventData,
  ...CreateAccountEventData,
  ...OnboardingEventData,
  ...FinishedEventData,
  ...GlobalEventData,
  ...DirverLicenseEventData,
  ...RefiDisqualifiedEventData,
  ...InviteV31TrackData,
  ...YearInReviewEventData,
};

export type EventName = keyof typeof EventData;

export interface TrackEventOpts<EventLabelType extends string> {
  eventLabel?: EventLabelType;
  eventMetadata?: Record<string, string | number | boolean>;
}

export const ssrTrackEvent = async <EventNameType extends EventName>(
  eventName: EventNameType,
  {
    eventMetadata,
    userID,
    sessionID,
  }: Pick<(typeof EventData)[EventNameType], 'eventMetadata'> & {
    userID?: string;
    sessionID?: string;
  } = {}
) => {
  const eventData = EventData[eventName] as Event<string>;
  const body = {
    clientType: 'serverless',
    releaseEnv: process.env.NEXT_PUBLIC_STAGE || 'dev',
    userID,
    sessionID,
    eventCategory: DEFAULT_EVENT_CATEGORY,
    ...eventData,
    eventMetadata,
  };
  try {
    await fetch(process.env.NEXT_PUBLIC_JA_URL as string, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (e) {
    console.error('ssrTrackEvent error', eventName, eventMetadata, userID, sessionID, e, e.message);
  }
};

/**
 * Track event for Jerry Analytics.
 *
 * @param event
 * @param opts
 */
export const trackEvent = async <EventNameType extends EventName>(
  eventName: EventNameType,
  opts?: Pick<(typeof EventData)[EventNameType], 'eventLabel' | 'eventMetadata'>
): Promise<void> => {
  const eventData = EventData[eventName] as Event<string>;
  const payload = {
    eventCategory: DEFAULT_EVENT_CATEGORY,
    ...eventData,
    ...opts,
  };

  await TrackEvent(payload);
};

/**
 * Track landing event.
 * @param appData
 */
export const trackPixelLanding = async (sessionID?: string): Promise<void> => {
  setLandingForPodScribePixel(sessionID);
};

/**
 * Track logged in / signedup user.
 *
 * @param user
 */
export const identifyUser = async (user: AnalyticsUser): Promise<void> => {
  await JAIdentify(user.id);
  await setUserForHJ(user);
  await setUserForFacebookPixel(user);
  await setUserForGoogle(user);
  await setUserForTikTokPixel(user);
  await setUserForRedditPixel(user);
  await setUserForTatariPixel(user);
  await setUserForSnapPixel(user);
};

/**
 * Track signup conversion.
 */
export const trackSignedUp = async (appData?: AppData): Promise<void> => {
  trackSignedUpForFacebook(appData);
  trackSignedUpForGoogle(appData);
  trackSignedUpForTaboola();
  trackSignedUpForTikTok(appData);
  trackSignedUpForReddit(appData);
  trackSignedUpForTatari();
  trackSignedUpForSnap();
  trackSignedUpForPodScribe(appData);
  await trackEvent('CreateAccountPhoneCodeAndAuthorize', {
    eventLabel: appData?.partner?.partner || 'None', // 'Signup | Phone Code & Authorize'
  });
};

/**
 * Track onboarded conversion.
 */
export const trackOnboarded = async (appData?: AppData): Promise<void> => {
  const { id: userID, partner: partnerData, selectedService } = appData || ({} as AppData);

  trackOnboardedForFacebook(appData);
  trackOnboardedForGoogle(appData);
  trackOnboardedForTaboola();
  trackOnboardedForTikTok(appData);
  trackOnboardedForReddit(appData);
  trackOnboardedForTatari();
  trackOnboardedForSnap();
  trackOnboardedForImpact(userID || '');
  trackOnboardedForPodScribe(appData);
  // Partner conversions
  if (partnerData) {
    reportConversion('onboarded', partnerData);
  }
  const services = join(
    filter(keys(selectedService), (key) => !!selectedService?.[key]),
    ','
  );

  await trackEvent('OnboardingValidated', {
    eventMetadata: {
      services, // 'Signup | Validated'
    },
  });
};

/**
 * Checks if marital status has been updated and sends a tracking event if it has.
 */
export const checkAndTrackMaritalStatusChange = (searchPolicyDrivers: Driver[], selectedDrivers: Driver[]) => {
  searchPolicyDrivers.forEach((policyDriver) => {
    // Find corresponding driver in the selectedDrivers
    const currentDriverIndex = selectedDrivers?.findIndex((d) => d.id === policyDriver.id);

    if (currentDriverIndex !== -1) {
      const currentDriver = selectedDrivers[currentDriverIndex];

      // Check whether maritalStatus has been changed
      if (currentDriver.maritalStatus !== policyDriver.maritalStatus) {
        // Retain only id, name, maritalStatus, and relationToUser for each driver
        const basicInfoPolicyDrivers = searchPolicyDrivers.map((d) => ({
          id: d.id,
          name: d.name,
          maritalStatus: d.maritalStatus,
          relationToUser: d.relationToUser,
        }));

        const basicInfoSelectedDrivers = selectedDrivers.map((d) => ({
          id: d.id,
          name: d.name,
          maritalStatus: d.maritalStatus,
          relationToUser: d.relationToUser,
        }));

        // Send a tracking event if the user updates their marital status compared to the pulled data
        void trackEvent('OnboardingMaritalStatusChanged', {
          eventMetadata: {
            policyDrivers: JSON.stringify(basicInfoPolicyDrivers).replace(/\\"/g, '"'),
            currentDrivers: JSON.stringify(basicInfoSelectedDrivers).replace(/\\"/g, '"'),
          },
        });
        return;
      }
    }
  });
};
